// Vertical KPI dashboards — pipeline / staffing / government / delivery / marketing.
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";

const money = (v: any) => (v == null ? "$0" : `$${Math.round(Number(v)).toLocaleString()}`);
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-2xl font-semibold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}
function Loading() { return <p className="text-sm text-muted-foreground">Loading…</p>; }
function Empty() { return <p className="py-6 text-center text-sm text-muted-foreground">No data yet.</p>; }

function useReport(vertical: string) {
  return useQuery({ queryKey: [`/reports/${vertical}`], queryFn: async () => (await api.get(`/reports/${vertical}`)).data as any });
}

const TABS = [
  { key: "pipeline", label: "Pipeline" },
  { key: "staffing", label: "Staffing" },
  { key: "government", label: "Government" },
  { key: "delivery", label: "Delivery" },
  { key: "marketing", label: "Marketing" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export default function DashboardsPage() {
  const [tab, setTab] = useState<TabKey>("pipeline");
  return (
    <>
      <PageHeader title="Dashboards" description="Vertical KPI views across the business." />
      <div className="px-6 pt-4">
        <div className="flex gap-1 border-b">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-t px-3 py-1.5 text-sm ${tab === t.key ? "border-b-2 border-primary font-medium text-primary" : "text-muted-foreground"}`}>{t.label}</button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {tab === "pipeline" && <PipelineTab />}
        {tab === "staffing" && <StaffingTab />}
        {tab === "government" && <GovernmentTab />}
        {tab === "delivery" && <DeliveryTab />}
        {tab === "marketing" && <MarketingTab />}
      </div>
    </>
  );
}

function PipelineTab() {
  const { data, isLoading } = useReport("pipeline");
  if (isLoading) return <Loading />;
  const entries = Object.entries(data?.byPipeline ?? {}) as [string, any][];
  const won = data?.wonThisQuarter;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {entries.map(([k, v]) => (
          <Stat key={k} label={`${k.replace(/_/g, " ")} pipeline`} value={money(v.totalValue)} sub={`${v.count} open · weighted ${money(v.weightedValue)}`} />
        ))}
        <Stat label="Won this quarter" value={money(won?._sum?.valueUsd)} sub={`${won?._count ?? 0} deals`} />
      </div>
      {entries.length === 0 && <Empty />}
    </div>
  );
}

function StaffingTab() {
  const { data, isLoading } = useReport("staffing");
  if (isLoading) return <Loading />;
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Stat label="Open job orders" value={data?.openOrders ?? 0} />
      <Stat label="Active placements" value={data?.activePlacements ?? 0} />
      <Stat label="Available bench" value={data?.availableBench ?? 0} />
      <Stat label="Submissions this month" value={data?.submissionsThisMonth ?? 0} />
      <Stat label="On bench 30–60 days" value={data?.benchAging?.aged30 ?? 0} />
      <Stat label="On bench 60+ days" value={data?.benchAging?.aged60 ?? 0} />
    </div>
  );
}

function GovernmentTab() {
  const { data, isLoading } = useReport("government");
  if (isLoading) return <Loading />;
  const dl = (data?.upcomingDeadlines ?? []) as any[];
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat label="Active pursuits" value={data?.active ?? 0} />
        <Stat label="Awarded" value={data?.awarded ?? 0} />
        <Stat label="Lost" value={data?.lost ?? 0} />
        <Stat label="Win rate" value={`${Math.round((data?.winRate ?? 0) * 100)}%`} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Upcoming deadlines (next 30 days)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <Thead><Tr><Th>Title</Th><Th>Agency</Th><Th>Stage</Th><Th>Due</Th></Tr></Thead>
            <Tbody>
              {dl.length === 0 && <Tr><Td colSpan={4} className="py-6 text-center text-muted-foreground">None due in 30 days.</Td></Tr>}
              {dl.map((o: any) => (
                <Tr key={o.id}>
                  <Td className="font-medium"><Link to={`/gov-opportunities/${o.id}`} className="hover:underline">{o.title}</Link></Td>
                  <Td className="text-muted-foreground">{o.agency}</Td>
                  <Td><Badge variant="secondary">{o.stage}</Badge></Td>
                  <Td>{fmtDate(o.responseDueDate)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function DeliveryTab() {
  const { data, isLoading } = useReport("delivery");
  if (isLoading) return <Loading />;
  const avg = data?.averages ?? {};
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Stat label="Active projects" value={data?.active ?? 0} />
      <Stat label="On hold" value={data?.onHold ?? 0} />
      <Stat label="Completed" value={data?.completed ?? 0} />
      <Stat label="Avg CSAT" value={avg.csatScore != null ? `${Number(avg.csatScore).toFixed(1)}/10` : "—"} sub={avg.npsScore != null ? `Avg NPS ${Math.round(Number(avg.npsScore))}` : undefined} />
    </div>
  );
}

function MarketingTab() {
  const { data, isLoading } = useReport("marketing");
  if (isLoading) return <Loading />;
  const bySource = (data?.bySource ?? []) as any[];
  const campaigns = (data?.campaigns ?? []) as any[];
  const max = Math.max(1, ...bySource.map((s) => s._count ?? 0));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="text-base">Leads by source</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {bySource.length === 0 && <Empty />}
          {bySource.map((s: any) => (
            <div key={s.source ?? "none"} className="flex items-center gap-2 text-sm">
              <span className="w-32 truncate">{s.source ?? "—"}</span>
              <div className="h-2 flex-1 rounded bg-muted"><div className="h-2 rounded bg-primary" style={{ width: `${Math.round(((s._count ?? 0) / max) * 100)}%` }} /></div>
              <span className="w-8 text-right tabular-nums text-muted-foreground">{s._count ?? 0}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Recent campaigns</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <Thead><Tr><Th>Name</Th><Th>Type</Th><Th>Start</Th></Tr></Thead>
            <Tbody>
              {campaigns.length === 0 && <Tr><Td colSpan={3} className="py-6 text-center text-muted-foreground">None.</Td></Tr>}
              {campaigns.map((c: any) => (
                <Tr key={c.id}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td className="text-muted-foreground">{c.type ?? "—"}</Td>
                  <Td>{fmtDate(c.startDate)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
